import { IAuthServiceV2 } from '@volkswagen-onehub/authservice';
import { v4 as uuid } from 'uuid';
import { ICommissionData } from './CommissionIdConverter';
import { VumProfileBrokenError } from './errors/VumProfileBrokenError';

export interface IVumUserVehicleRelationUpdate {
    readonly vehicleNickname?: string,
    readonly licensePlate?: string,
    readonly vehicle?: {
        readonly vin?: string,
        readonly commissionId?: string
    }
}

export interface IVumUserVehicleRelation {
    readonly vehicleNickname?: string,
    readonly role?: string,
    readonly enrollmentStatus?: string,
    readonly licensePlate?: string,
    readonly primaryCar?: string,
    readonly dealerId?: string,
    readonly dealerBrandCode?: string,
    readonly allocatedDealerCountry?: string,
    readonly preferredContact?: string,
    readonly vehicle?: {
        readonly vin?: string,
        readonly commissionId?: string,
        readonly modBackend?: string
    }
}

export interface IVumUser {
    readonly mbbUserId: string;
}

export interface IVumUserVehicleRelationsResponse {
    readonly relations: IVumUserVehicleRelation[],
    readonly user: IVumUser
}

export interface IVumUserVehicleRelationResponse {
    readonly relation: IVumUserVehicleRelation;
}

export interface IJSONPatchOperation {
    readonly op: string,
    readonly path: string,
    readonly value: string | null,
}

export class VumClient {

    private readonly resourceHostId: string;

    constructor(private readonly authService: IAuthServiceV2, stage: string) {
        this.resourceHostId = `myvw-vum-${stage}`
    }

    defaultBodyRequestHeaders = {
        'Content-Type': 'application/json',
    };

    // https://vum-sandbox-documentation.apps.emea.vwapps.io - getRelationsByUserWithMetadata
    public async getUserVehicleRelations(): Promise<IVumUserVehicleRelationsResponse> {
        const response = await this.authService.get(this.resourceHostId, 'v2/users/me/relations',
            { traceId: uuid() });

        if (response.status === 422) {
            throw new VumProfileBrokenError();
        }

        if (!response.ok) {
            throw new Error(); // TODO: How to get error message
        }

        return await response.json();
    }

    // https://vum-sandbox-documentation.apps.emea.vwapps.io - getRelationsByUserAndVin
    public async getUserVehicleRelation(vin: string): Promise<IVumUserVehicleRelationResponse> {
        const response = await this.authService.get(this.resourceHostId, `v2/users/me/relations/${vin}`,
            { traceId: uuid() });

        if (response.status === 422) {
            throw new VumProfileBrokenError();
        }

        if (!response.ok) {
            throw new Error(); // TODO: How to get error message
        }

        return await response.json();
    }

    // https://vum-sandbox-documentation.apps.emea.vwapps.io
    public async patchVinVehicle(vin: string, dataToUpdate: IJSONPatchOperation []): Promise<void> {
        await this.authService.patch(this.resourceHostId,
            `dataStorageManagement/users/me/relations?vin=${vin}`, JSON.stringify(dataToUpdate),
            { ...this.defaultBodyRequestHeaders, traceId: uuid() });
    }

    // https://vum-sandbox-documentation.apps.emea.vwapps.io
    public async patchCommissionVehicle(commissionData: ICommissionData, dataToUpdate: IJSONPatchOperation []): Promise<void> {
        await this.authService.patch(this.resourceHostId, 'dataStorageManagement/users/me/relations?commissionId=' +
            `${commissionData.commissionNumber}-${commissionData.bid}-${commissionData.commissionNumberYear}`,
            JSON.stringify(dataToUpdate), { ...this.defaultBodyRequestHeaders, traceId: uuid() });
    }

    // https://vum-sandbox-documentation.apps.emea.vwapps.io
    public async updateUserVehicleRelation(relation: IVumUserVehicleRelationUpdate): Promise<void> {
        const response = await this.authService.put(this.resourceHostId,
            'v1/dataStorageManagement/users/me/relations', JSON.stringify(relation),
            { ...this.defaultBodyRequestHeaders, traceId: uuid() });

        if (!response.ok) {
            throw new Error(); // TODO: How to get error message
        }
    }

    // https://vum-sandbox-documentation.apps.emea.vwapps.io
    public async addVinToUserVehicle(commissionId: string, vin: string): Promise<void> {
        const response = await this.authService.put(this.resourceHostId,
            `v1/dataStorageManagement/users/me/vehicles/${commissionId}/vin`,
            JSON.stringify({ vin }), { ...this.defaultBodyRequestHeaders, traceId: uuid() });

        if (!response.ok) {
            throw new Error(); // TODO: How to get error message
        }
    }

    // https://vum-sandbox-documentation.apps.emea.vwapps.io - primaryUserReset
    public async resetPrimaryUser(vin: string): Promise<void> {
        const validStatusCodes = [204, 404, 409];

        const response = await this.authService.put(this.resourceHostId,
            `primaryUserReset/users/me/vehicles/${vin}`, undefined, { traceId: uuid() });

        if (!validStatusCodes.includes(response.status)) {
            throw new Error(`Request failed with status code ${response.status}`);
        }
    }

    // https://vum-sandbox-documentation.apps.emea.vwapps.io - roleReset
    public async resetNonPrimaryUser(vin: string): Promise<void> {
        const response = await this.authService.put(this.resourceHostId, `users/me/vehicles/${vin}`,
            undefined, { traceId: uuid() });

        if (!response.ok) {
            throw new Error(`Request failed with status code ${response.status}`);
        }
    }

    // https://vum-sandbox-documentation.apps.emea.vwapps.io
    public async addUserVehicleRelation(userVehicleRelation: IVumUserVehicleRelationUpdate): Promise<void> {
        const response = await this.authService.post(this.resourceHostId,
            'v1/dataStorageManagement/users/me/relations', JSON.stringify(userVehicleRelation),
            { ...this.defaultBodyRequestHeaders, traceId: uuid() });

        if (!response.ok) {
            throw new Error(); // TODO: How to get error message
        }
    }

    // https://vum-sandbox-documentation.apps.emea.vwapps.io - deleteVehicleInGarage
    public async deleteUserVehicleRelation(userVehicleRelation: IVumUserVehicleRelationUpdate): Promise<void> {
        let deleteUserVehicleRelationUrl = 'users/me/vehicles';
        if (userVehicleRelation.vehicle?.vin) {
            deleteUserVehicleRelationUrl += `?vin=${userVehicleRelation.vehicle.vin}`;
        } else {
            deleteUserVehicleRelationUrl += `?commissionId=${userVehicleRelation.vehicle?.commissionId}`;
        }

        const response = await this.authService.delete(this.resourceHostId, deleteUserVehicleRelationUrl,
            undefined, { traceId: uuid() });

        if (!response.ok) {
            throw new Error(); // TODO: How to get error message
        }
    }

    // https://vum-sandbox-documentation.apps.emea.vwapps.io - deleteUserAccount
    public async deleteUser(): Promise<void> {
        const response = await this.authService.delete(this.resourceHostId, 'v1/users/me',
            undefined, { traceId: uuid() });

        if (!response.ok) {
            throw new Error(); // TODO: How to get error message
        }
    }

    // https://vum-sandbox-documentation.apps.emea.vwapps.io - Delete Guest or Secondary User by Primary User
    public async removeUserFromVehicle(userId: string, vin: string): Promise<void> {
        const response = await this.authService.delete(this.resourceHostId,
            `v1/users/${userId}/vehicles/${vin}`, undefined, { traceId: uuid() });

        if (!response.ok) {
            throw new Error(); // TODO: How to get error message
        }
    }
}